import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ThemeContext, type Theme, type ThemeState } from '@/hooks/use-theme'

const COOKIE = 'ahri_theme'

function readInitialTheme(): Theme {
  try {
    const cookie = document.cookie.match(/(?:^|;\s*)ahri_theme=([^;]*)/)?.[1]
    const value = cookie ?? localStorage.getItem('theme')
    if (value === 'light' || value === 'dark' || value === 'system') return value
  } catch {
    // ignore
  }
  return 'system'
}

const darkQuery = () => window.matchMedia('(prefers-color-scheme: dark)')

/**
 * Light / dark / system, shared with the other Hirax sites through the
 * `ahri_theme` cookie on .ahri.cloud (the inline script in index.html applies
 * it before first paint).
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme)
  const [systemDark, setSystemDark] = useState(() => darkQuery().matches)

  useEffect(() => {
    const mq = darkQuery()
    const onChange = () => setSystemDark(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const resolved: 'light' | 'dark' = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme

  useEffect(() => {
    const root = document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(resolved)
    root.style.colorScheme = resolved
  }, [resolved])

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next)
    try {
      localStorage.setItem('theme', next)
    } catch {
      // ignore
    }
    const host = window.location.hostname
    const domain = host === 'ahri.cloud' || host.endsWith('.ahri.cloud') ? '; Domain=.ahri.cloud' : ''
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${COOKIE}=${next}; Path=/; Max-Age=31536000; SameSite=Lax${domain}${secure}`
  }, [])

  const value = useMemo<ThemeState>(() => ({ theme, resolved, setTheme }), [theme, resolved, setTheme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
